import React, {lazy, Suspense, useEffect, useMemo, useRef, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {
    CheckOutlined,
    ChevronRightOutlined,
    DarkModeOutlined,
    LightModeOutlined,
    LogoutOutlined,
    MenuOutlined,
    MenuOpenOutlined,
    PersonOutlineOutlined,
    SearchOutlined,
    SettingsBrightnessOutlined
} from "@mui/icons-material";
import {logout, selectCurrentUser} from "../../features/auth/authSlice";
import {setThemeMode} from "../../app/theme/themeSlice";
import {useTheme} from "../../app/theme/ThemeProvider";
import {findTrail} from "../navigation";
import RootAddress from "../../app/RootAddress";
import "./topbar.css";

const NotificationsBell = lazy(() => import("./NotificationsBell"));

const themeOptions = [
    {mode: "light", title: "Светлая", icon: <LightModeOutlined fontSize="small"/>},
    {mode: "dark", title: "Тёмная", icon: <DarkModeOutlined fontSize="small"/>},
    {mode: "system", title: "Как в системе", icon: <SettingsBrightnessOutlined fontSize="small"/>}
];

const getInitials = (user) => {
    const first = user?.firstName?.trim()?.[0] || "";
    const last = user?.lastName?.trim()?.[0] || "";
    const initials = (first + last).toUpperCase();
    return initials || user?.email?.trim()?.[0]?.toUpperCase() || "A";
};

const getAvatarUrl = (avatar) => {
    if (!avatar) {
        return null;
    }
    if (avatar.startsWith("http://") || avatar.startsWith("https://")) {
        return avatar;
    }
    return RootAddress.getIStudyURL + avatar;
};

const Topbar = ({collapsed, onToggleCollapse, onOpenMobile, onOpenCommand}) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const user = useSelector(selectCurrentUser);
    const {mode, resolvedMode} = useTheme();

    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [themeMenuOpen, setThemeMenuOpen] = useState(false);
    const userMenuRef = useRef(null);
    const themeMenuRef = useRef(null);

    const trail = useMemo(() => findTrail(location.pathname), [location.pathname]);

    useEffect(() => {
        const onPointerDown = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setUserMenuOpen(false);
            }
            if (themeMenuRef.current && !themeMenuRef.current.contains(event.target)) {
                setThemeMenuOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setUserMenuOpen(false);
                setThemeMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    const avatarUrl = getAvatarUrl(user?.avatar);
    const initials = getInitials(user);
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "Администратор";

    return (
        <header className="ui-topbar">
            <div className="ui-topbar__left">
                <button
                    type="button"
                    className="ui-icon-button ui-topbar__burger"
                    onClick={onOpenMobile}
                    aria-label="Открыть меню"
                >
                    <MenuOutlined/>
                </button>
                <button
                    type="button"
                    className="ui-icon-button ui-topbar__collapse"
                    onClick={onToggleCollapse}
                    aria-label={collapsed ? "Развернуть меню" : "Свернуть меню"}
                    title={collapsed ? "Развернуть меню" : "Свернуть меню"}
                >
                    {collapsed ? <MenuOutlined/> : <MenuOpenOutlined/>}
                </button>

                <nav className="ui-breadcrumbs" aria-label="Хлебные крошки">
                    <Link to="/panel" className="ui-breadcrumbs__item">Панель</Link>
                    {trail && (
                        <>
                            <ChevronRightOutlined className="ui-breadcrumbs__sep" fontSize="small"/>
                            <span className="ui-breadcrumbs__item">{trail.group.title}</span>
                        </>
                    )}
                    {trail && (trail.child || trail.item) && (
                        <>
                            <ChevronRightOutlined className="ui-breadcrumbs__sep" fontSize="small"/>
                            <span className="ui-breadcrumbs__item is-current">
                                {trail.child ? `${trail.item.title} · ${trail.child.title}` : trail.item.title}
                            </span>
                        </>
                    )}
                </nav>
            </div>

            <div className="ui-topbar__right">
                <button
                    type="button"
                    className="ui-topbar__search"
                    onClick={onOpenCommand}
                    aria-label="Поиск по разделам"
                >
                    <SearchOutlined fontSize="small"/>
                    <span>Поиск</span>
                    <kbd>Ctrl K</kbd>
                </button>

                <Suspense fallback={<span className="ui-icon-button" aria-hidden="true"/>}>
                    <NotificationsBell/>
                </Suspense>

                <div className="ui-popover" ref={themeMenuRef}>
                    <button
                        type="button"
                        className="ui-icon-button"
                        onClick={() => setThemeMenuOpen((open) => !open)}
                        aria-label="Тема оформления"
                        aria-haspopup="menu"
                        aria-expanded={themeMenuOpen}
                        title="Тема оформления"
                    >
                        {resolvedMode === "dark" ? <DarkModeOutlined/> : <LightModeOutlined/>}
                    </button>
                    {themeMenuOpen && (
                        <div className="ui-menu" role="menu">
                            <div className="ui-menu__label">Оформление</div>
                            {themeOptions.map((option) => (
                                <button
                                    key={option.mode}
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={mode === option.mode}
                                    className={`ui-menu__item${mode === option.mode ? " is-selected" : ""}`}
                                    onClick={() => {
                                        dispatch(setThemeMode(option.mode));
                                        setThemeMenuOpen(false);
                                    }}
                                >
                                    <span className="ui-menu__icon">{option.icon}</span>
                                    <span>{option.title}</span>
                                    {mode === option.mode && <CheckOutlined fontSize="small"/>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="ui-popover" ref={userMenuRef}>
                    <button
                        type="button"
                        className="ui-user"
                        onClick={() => setUserMenuOpen((open) => !open)}
                        aria-haspopup="menu"
                        aria-expanded={userMenuOpen}
                    >
                        {avatarUrl ? (
                            <img src={avatarUrl} alt="" className="ui-avatar"/>
                        ) : (
                            <span className="ui-avatar ui-avatar--fallback">{initials}</span>
                        )}
                        <span className="ui-user__text">
                            <span className="ui-user__name">{fullName}</span>
                            <span className="ui-user__role">{user?.email || "Администратор"}</span>
                        </span>
                    </button>
                    {userMenuOpen && (
                        <div className="ui-menu ui-menu--right" role="menu">
                            <div className="ui-menu__header">
                                <div className="ui-menu__name">{fullName}</div>
                                <div className="ui-menu__email">{user?.email}</div>
                            </div>
                            <Link to="/panel" className="ui-menu__item" role="menuitem" onClick={() => setUserMenuOpen(false)}>
                                <span className="ui-menu__icon"><PersonOutlineOutlined fontSize="small"/></span>
                                <span>Профиль и сводка</span>
                            </Link>
                            <button
                                type="button"
                                role="menuitem"
                                className="ui-menu__item ui-menu__item--danger"
                                onClick={() => dispatch(logout())}
                            >
                                <span className="ui-menu__icon"><LogoutOutlined fontSize="small"/></span>
                                <span>Выйти</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Topbar;

